import { BandLimitedOscillator, type Waveform } from '../../packages/mod-001/src/oscillator.js';
import type { LiveFilterReadback, LiveFilterSettings } from './live-modulated-filter.js';

export const KEYBOARD_CODE_TO_MIDI = Object.freeze({
  KeyZ: 60,
  KeyS: 61,
  KeyX: 62,
  KeyD: 63,
  KeyC: 64,
  KeyV: 65,
  KeyG: 66,
  KeyB: 67,
  KeyH: 68,
  KeyN: 69,
  KeyJ: 70,
  KeyM: 71,
  Comma: 72,
  KeyL: 73,
  Period: 74,
  Semicolon: 75,
  Slash: 76,
} as const);

export interface VoiceSink {
  noteOn(key: string, note: number, velocity: number): void;
  noteOff(key: string): void;
  panic(): void;
}

export interface KeyboardNoteEvent {
  readonly code: string;
  readonly repeat?: boolean;
  readonly ctrlKey?: boolean;
  readonly altKey?: boolean;
  readonly metaKey?: boolean;
  readonly editable?: boolean;
}

export interface NormalizedMidiNote {
  readonly type: 'noteon' | 'noteoff';
  readonly sourceId: string;
  readonly channel: number;
  readonly note: number;
  readonly velocity: number;
}

const midiByte = (value: number | undefined): value is number =>
  Number.isInteger(value) && value! >= 0 && value! <= 127;

export const midiToFrequency = (note: number): number => {
  if (!Number.isInteger(note) || note < 0 || note > 127) throw new RangeError('MIDI note must be an integer in [0, 127]');
  return 440 * (2 ** ((note - 69) / 12));
};

export const parseMidiMessage = (
  data: ArrayLike<number>,
  sourceId: string,
): NormalizedMidiNote | null => {
  const status = data[0];
  const note = data[1];
  const velocity = data[2];
  if (!Number.isInteger(status) || !midiByte(note) || !midiByte(velocity)) return null;
  const command = status! & 0xf0;
  if (command !== 0x80 && command !== 0x90) return null;
  const type = command === 0x80 || velocity === 0 ? 'noteoff' : 'noteon';
  return Object.freeze({
    type,
    sourceId,
    channel: (status! & 0x0f) + 1,
    note,
    velocity: velocity / 127,
  });
};

export class LiveInstrumentController {
  private readonly active = new Set<string>();

  constructor(private readonly sink: VoiceSink) {}

  get activeVoiceCount(): number {
    return this.active.size;
  }

  keyDown(event: KeyboardNoteEvent): boolean {
    const note = KEYBOARD_CODE_TO_MIDI[event.code as keyof typeof KEYBOARD_CODE_TO_MIDI];
    if (note === undefined || event.repeat || event.ctrlKey || event.altKey || event.metaKey || event.editable) return false;
    const key = `keyboard:${event.code}`;
    if (this.active.has(key)) return false;
    this.active.add(key);
    this.sink.noteOn(key, note, 0.8);
    return true;
  }

  keyUp(event: KeyboardNoteEvent): boolean {
    if (KEYBOARD_CODE_TO_MIDI[event.code as keyof typeof KEYBOARD_CODE_TO_MIDI] === undefined) return false;
    const key = `keyboard:${event.code}`;
    if (!this.active.delete(key)) return false;
    this.sink.noteOff(key);
    return true;
  }

  midi(data: ArrayLike<number>, sourceId: string): boolean {
    const message = parseMidiMessage(data, sourceId);
    if (!message) return false;
    const key = `midi:${message.sourceId}:${message.channel}:${message.note}`;
    if (message.type === 'noteon') {
      if (this.active.has(key)) return false;
      this.active.add(key);
      this.sink.noteOn(key, message.note, message.velocity);
      return true;
    }
    if (!this.active.delete(key)) return false;
    this.sink.noteOff(key);
    return true;
  }

  panic(): void {
    this.active.clear();
    this.sink.panic();
  }
}

export interface EnvelopeSettings {
  readonly attack: number;
  readonly decay: number;
  readonly sustain: number;
  readonly release: number;
}

export interface OscillatorSettings {
  readonly waveform: Waveform;
  readonly pulseWidth: number;
  readonly phaseDistortion: number;
  readonly reversePhase: boolean;
  readonly invertPolarity: boolean;
}

export interface DualOscillatorSettings {
  readonly oscillatorA: OscillatorSettings;
  readonly oscillatorB: OscillatorSettings;
  readonly mix: number;
}

export interface LinearCrossfadeReadback {
  readonly sample: number;
  readonly gainA: number;
  readonly gainB: number;
}

export const linearCrossfade = (sampleA: number, sampleB: number, mix: number): LinearCrossfadeReadback => {
  const amount = bounded(mix, 0, 1);
  const gainA = 1 - amount;
  const gainB = amount;
  return Object.freeze({ sample: sampleA * gainA + sampleB * gainB, gainA, gainB });
};

export const MAX_LIVE_VOICES = 16;
// 16 fully coherent voices sum to <= 0.24 before the shared resonant filter.
export const LIVE_VOICE_GAIN = 0.015;

export interface CumulativeMeterReadback {
  readonly cumulativePeak: number;
  readonly cumulativeRms: number;
  readonly cumulativeFrames: number;
  readonly overRangeSamples: number;
}

export class CumulativeMeter {
  private peak = 0;
  private squares = 0;
  private frames = 0;
  private overRange = 0;

  measure(samples: Float32Array): void {
    for (const sample of samples) {
      this.peak = Math.max(this.peak, Math.abs(sample));
      this.squares += sample * sample;
      this.frames += 1;
      if (Math.abs(sample) >= 1) this.overRange += 1;
    }
  }

  read(): CumulativeMeterReadback {
    return Object.freeze({
      cumulativePeak: this.peak,
      cumulativeRms: this.frames > 0 ? Math.sqrt(this.squares / this.frames) : 0,
      cumulativeFrames: this.frames,
      overRangeSamples: this.overRange,
    });
  }
}

export interface RenderedOscillatorCycle {
  readonly moduleId: 'mir.mod-001';
  readonly samples: Float32Array;
  readonly baseFrequency: number;
  readonly playbackRate: number;
}

export const renderMod001Cycle = (
  sampleRate: number,
  frequency: number,
  settings: OscillatorSettings,
): RenderedOscillatorCycle => {
  if (!Number.isFinite(sampleRate) || sampleRate <= 0) throw new RangeError('sampleRate must be greater than zero');
  if (!Number.isFinite(frequency) || frequency <= 0 || frequency >= sampleRate / 2) {
    throw new RangeError('frequency must be in (0, sampleRate / 2)');
  }
  const frames = Math.max(2, Math.round(sampleRate / frequency));
  const baseFrequency = sampleRate / frames;
  const oscillator = new BandLimitedOscillator({
    sampleRate,
    waveform: settings.waveform,
    frequency: baseFrequency,
    pulseWidth: bounded(settings.pulseWidth, 0.01, 0.99),
    phaseDistortion: bounded(settings.phaseDistortion, -0.95, 0.95),
    reversePhase: settings.reversePhase,
    invertPolarity: settings.invertPolarity,
  });
  const samples = new Float32Array(frames);
  oscillator.processInto(samples);
  return Object.freeze({
    moduleId: 'mir.mod-001',
    samples,
    baseFrequency,
    playbackRate: frequency / baseFrequency,
  });
};

interface ActiveWebAudioVoice {
  readonly oscillators: readonly [AudioBufferSourceNode, AudioBufferSourceNode];
  readonly mixGains: readonly [GainNode, GainNode];
  readonly gain: GainNode;
}

const bounded = (value: number, minimum: number, maximum: number): number =>
  Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum));

export class WebAudioVoiceSink implements VoiceSink {
  private readonly voices = new Map<string, ActiveWebAudioVoice>();
  private readonly pending = new Map<string, symbol>();
  private filterNode: AudioWorkletNode | undefined;
  private filterNodePromise: Promise<AudioWorkletNode> | undefined;

  constructor(
    private readonly getContext: () => AudioContext,
    private readonly getEnvelope: () => EnvelopeSettings,
    private readonly getOscillators: () => DualOscillatorSettings,
    private readonly getFilter: () => LiveFilterSettings,
    private readonly onVoiceCount?: (count: number) => void,
    private readonly onFilterReadback?: (readback: LiveFilterReadback & {
      readonly peak: number;
      readonly rms: number;
      readonly cumulativePeak: number;
      readonly cumulativeRms: number;
      readonly cumulativeFrames: number;
      readonly overRangeSamples: number;
    }) => void,
    private readonly onError?: (error: Error) => void,
  ) {}

  get voiceCount(): number {
    return this.voices.size;
  }

  noteOn(key: string, note: number, velocity: number): void {
    const context = this.getContext();
    void context.resume();
    const token = Symbol(key);
    this.pending.set(key, token);
    void this.startVoice(context, key, token, note, velocity).catch((cause: unknown) => {
      if (this.pending.get(key) === token) this.pending.delete(key);
      this.report();
      this.onError?.(cause instanceof Error ? cause : new Error(String(cause)));
    });
    this.report();
  }

  noteOff(key: string): void {
    if (this.pending.delete(key)) this.report();
    const voice = this.voices.get(key);
    if (!voice) return;
    this.releaseVoice(key, voice, this.safeEnvelope().release);
  }

  panic(): void {
    this.pending.clear();
    for (const [key, voice] of this.voices) this.releaseVoice(key, voice, 0.01);
    this.report();
  }

  updateFilterSettings(): void {
    this.filterNode?.port.postMessage({ type: 'settings', settings: this.getFilter() });
  }

  private async startVoice(context: AudioContext, key: string, token: symbol, note: number, velocity: number): Promise<void> {
    const filterNode = await this.ensureFilterNode(context);
    if (this.pending.get(key) !== token) return;
    this.pending.delete(key);
    if (this.voices.size >= MAX_LIVE_VOICES) {
      const oldest = this.voices.entries().next().value as [string, ActiveWebAudioVoice] | undefined;
      if (oldest) this.releaseVoice(oldest[0], oldest[1], 0.005);
    }
    const envelope = this.safeEnvelope();
    const now = context.currentTime;
    const settings = this.getOscillators();
    const frequency = midiToFrequency(note);
    const makeOscillator = (oscillatorSettings: OscillatorSettings): AudioBufferSourceNode => {
      const rendered = renderMod001Cycle(context.sampleRate, frequency, oscillatorSettings);
      const oscillator = context.createBufferSource();
      const buffer = context.createBuffer(1, rendered.samples.length, context.sampleRate);
      buffer.getChannelData(0).set(rendered.samples);
      oscillator.buffer = buffer;
      oscillator.loop = true;
      oscillator.playbackRate.setValueAtTime(rendered.playbackRate, now);
      return oscillator;
    };
    const oscillators = [makeOscillator(settings.oscillatorA), makeOscillator(settings.oscillatorB)] as const;
    const crossfade = linearCrossfade(0, 0, settings.mix);
    const mixGains = [context.createGain(), context.createGain()] as const;
    mixGains[0].gain.setValueAtTime(crossfade.gainA, now);
    mixGains[1].gain.setValueAtTime(crossfade.gainB, now);
    const gain = context.createGain();
    gain.gain.setValueAtTime(0, now);
    const level = bounded(velocity, 0, 1) * LIVE_VOICE_GAIN;
    gain.gain.linearRampToValueAtTime(level, now + envelope.attack);
    gain.gain.linearRampToValueAtTime(level * envelope.sustain, now + envelope.attack + envelope.decay);
    oscillators[0].connect(mixGains[0]);
    oscillators[1].connect(mixGains[1]);
    mixGains[0].connect(gain);
    mixGains[1].connect(gain);
    gain.connect(filterNode);
    oscillators[0].onended = () => {
      if (this.voices.get(key)?.oscillators[0] === oscillators[0]) {
        this.voices.delete(key);
        this.report();
      }
      gain.disconnect();
      for (const mixGain of mixGains) mixGain.disconnect();
      for (const oscillator of oscillators) oscillator.disconnect();
    };
    this.voices.set(key, { oscillators, mixGains, gain });
    filterNode.port.postMessage({ type: 'note-on', key });
    for (const oscillator of oscillators) oscillator.start(now);
    this.report();
  }

  private ensureFilterNode(context: AudioContext): Promise<AudioWorkletNode> {
    if (this.filterNode) return Promise.resolve(this.filterNode);
    if (!this.filterNodePromise) {
      this.filterNodePromise = context.audioWorklet.addModule('/live-filter-worklet.js').then(() => {
        const node = new AudioWorkletNode(context, 'allmost-live-filter', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [1],
          channelCount: 1,
          channelCountMode: 'explicit',
        });
        node.port.onmessage = (event: MessageEvent<LiveFilterReadback & {
          readonly type: 'meter';
          readonly peak: number;
          readonly rms: number;
          readonly cumulativePeak: number;
          readonly cumulativeRms: number;
          readonly cumulativeFrames: number;
          readonly overRangeSamples: number;
        }>) => {
          if (event.data?.type === 'meter') this.onFilterReadback?.(event.data);
        };
        node.port.postMessage({ type: 'settings', settings: this.getFilter() });
        const equalizer = context.createBiquadFilter();
        equalizer.type = 'peaking';
        equalizer.frequency.setValueAtTime(1_000, context.currentTime);
        equalizer.Q.setValueAtTime(Math.SQRT1_2, context.currentTime);
        equalizer.gain.setValueAtTime(0, context.currentTime);
        const mixer = context.createGain();
        mixer.gain.setValueAtTime(1, context.currentTime);
        const dynamics = context.createDynamicsCompressor();
        dynamics.threshold.setValueAtTime(-18, context.currentTime);
        dynamics.ratio.setValueAtTime(4, context.currentTime);
        dynamics.attack.setValueAtTime(0.005, context.currentTime);
        dynamics.release.setValueAtTime(0.12, context.currentTime);
        const safety = context.createWaveShaper();
        const curve = new Float32Array(2049);
        for (let index = 0; index < curve.length; index += 1) {
          const input = index * 2 / (curve.length - 1) - 1;
          curve[index] = Math.tanh(input) * 0.891250938;
        }
        safety.curve = curve;
        safety.oversample = '2x';
        node.connect(equalizer).connect(mixer).connect(dynamics).connect(safety).connect(context.destination);
        this.filterNode = node;
        return node;
      });
    }
    return this.filterNodePromise;
  }

  private safeEnvelope(): EnvelopeSettings {
    const value = this.getEnvelope();
    return Object.freeze({
      attack: bounded(value.attack, 0.001, 5),
      decay: bounded(value.decay, 0.001, 5),
      sustain: bounded(value.sustain, 0, 1),
      release: bounded(value.release, 0.005, 10),
    });
  }

  private releaseVoice(key: string, voice: ActiveWebAudioVoice, release: number): void {
    const context = this.getContext();
    const now = context.currentTime;
    const parameter = voice.gain.gain;
    parameter.cancelAndHoldAtTime(now);
    parameter.linearRampToValueAtTime(0, now + release);
    this.filterNode?.port.postMessage({ type: 'note-off', key });
    for (const oscillator of voice.oscillators) oscillator.stop(now + release + 0.01);
    this.voices.delete(key);
    this.report();
  }

  private report(): void {
    this.onVoiceCount?.(this.voices.size);
  }
}
